import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { usePageSEO } from "@/lib/seo";
import resolveImageUrl from "@/lib/imageUrl";

/**
 * Public page for the General Assembly & Live Raffle Draw (25 Oct 2026).
 *
 * - Reads `/api/events/general-assembly-raffle-draw-2026` which exposes a
 *   shaped subset of the editable event record. Every field is CMS-driven:
 *   title, date, start/end time, timezone, venue, address (+ visibility),
 *   description, livestream URL, CTA label + URL, cover image.
 * - Blank/approved values are preserved — the page never invents content.
 * - Address is only rendered when the editor sets `address_visible = true`.
 */
export default function GeneralAssembly() {
  const { lang } = useI18n();
  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";
  const [state, setState] = useState({ loading: true, event: null, error: null });

  const load = async () => {
    setState({ loading: true, event: null, error: null });
    try {
      const r = await api.get("/events/general-assembly-raffle-draw-2026", { timeout: 12000 });
      setState({ loading: false, event: r.data?.event || null, error: null });
    } catch (e) {
      setState({ loading: false, event: null, error: String(e?.message || e) });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // SEO — fall back to safe defaults if the editor has left SEO blank.
  const seoTitle = T(state.event?.seo?.title) || "Community Meeting & Live Raffle Draw · 25 October 2026";
  const seoDesc = T(state.event?.seo?.description) || "DANAmark Community Meeting and live raffle draw on 25 October 2026 at 17:00 Copenhagen time.";
  usePageSEO(seoTitle, seoDesc);

  if (state.loading) {
    return (
      <div className="container-x max-editorial py-24 text-ink/40 text-sm" data-testid="ga-loading">
        Loading…
      </div>
    );
  }

  if (state.error && !state.event) {
    return (
      <div className="container-x max-editorial py-24 text-center" data-testid="ga-error">
        <div className="label-mono text-terracotta text-[10px]">/events</div>
        <h1 className="mt-3 font-editorial text-3xl">We couldn't load this page right now.</h1>
        <button onClick={load} data-testid="ga-retry"
                className="mt-6 inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
          Try again
        </button>
      </div>
    );
  }

  if (!state.event) {
    // Event exists in DB but is draft/cancelled/hidden, or never seeded.
    return (
      <div className="container-x max-editorial py-24 text-center" data-testid="ga-unpublished">
        <div className="label-mono text-terracotta text-[10px]">/events</div>
        <h1 className="mt-3 font-editorial text-3xl">This page isn't published yet.</h1>
        <p className="mt-4 text-ink/60 max-w-md mx-auto">The event will appear here once the organiser publishes it.</p>
      </div>
    );
  }

  const e = state.event;
  const dateLine = formatDateLong(e.date_iso, lang);
  const timeLine = formatTimeRange(e.start_time, e.end_time);
  const cover = e.cover_image ? resolveImageUrl(e.cover_image) : "";
  const location = [e.venue, e.city].filter(Boolean).join(" · ");

  return (
    <div className="bg-base text-ink" data-testid="general-assembly-page">
      {/* Hero / header */}
      <section className="relative overflow-hidden bg-ink text-white">
        {cover && (
          <>
            <img src={cover} alt={T(e.title)} loading="eager"
                 className="absolute inset-0 w-full h-full object-cover opacity-55" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/35" />
          </>
        )}
        <div className="relative container-x max-editorial py-20 md:py-28">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]"
               data-testid="ga-eyebrow">
            {dateLine ? dateLine.toUpperCase() : "25 OCTOBER 2026"}
          </div>
          <h1 className="mt-5 font-editorial text-[42px] md:text-[72px] leading-[0.98] tracking-tighter max-w-4xl"
              data-testid="ga-title">
            {T(e.title) || "Community Meeting & Live Raffle Draw"}
          </h1>
          <dl className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 max-w-3xl">
            <Fact k={({ en: "Date", da: "Dato", es: "Fecha" })[lang]} v={dateLine} testid="ga-date" />
            {timeLine && <Fact k={({ en: "Community Meeting", da: "Fællesskabsmøde", es: "Encuentro comunitario" })[lang]}
                               v={`${timeLine} (${tzLabel(e.tz, lang)})`} testid="ga-time" />}
            {e.raffle_draw_time && (
              <Fact k={({ en: "Live Raffle Draw", da: "Live-lodtrækning", es: "Sorteo en directo" })[lang]}
                    v={`${e.raffle_draw_time} (${tzLabel(e.tz, lang)})`} testid="ga-raffle-time" />
            )}
            {location && <Fact k={({ en: "Location", da: "Lokation", es: "Ubicación" })[lang]} v={location} testid="ga-location" />}
            {/* Address only rendered when address_visible is true. The public
                API also strips the field when visibility is off. */}
            {e.address_visible && e.address && (
              <Fact k={({ en: "Address", da: "Adresse", es: "Dirección" })[lang]} v={e.address} testid="ga-address" />
            )}
          </dl>
        </div>
      </section>

      {/* Description */}
      {T(e.description) && (
        <section className="container-x max-editorial py-16 md:py-24 max-w-3xl" data-testid="ga-description">
          {String(T(e.description) || "").replace(/\r\n/g, "\n").split(/\n{2,}/).map(s => s.trim()).filter(Boolean).map((p, i) => (
            <p key={i} className="mt-4 text-[16px] md:text-[18px] leading-relaxed whitespace-pre-line text-ink/85">{p}</p>
          ))}
        </section>
      )}

      {/* CTA / livestream */}
      {(T(e.cta_label) && e.cta_url) || e.livestream_url ? (
        <section className="border-t border-line">
          <div className="container-x max-editorial py-14 md:py-20 flex flex-wrap gap-4">
            {T(e.cta_label) && e.cta_url && (
              <a href={e.cta_url}
                 target={/^https?:\/\//i.test(e.cta_url) ? "_blank" : undefined}
                 rel={/^https?:\/\//i.test(e.cta_url) ? "noopener noreferrer" : undefined}
                 data-testid="ga-cta"
                 className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-7 py-3.5 text-sm tracking-[0.16em] uppercase">
                {T(e.cta_label)}
              </a>
            )}
            {e.livestream_url && (
              <a href={e.livestream_url} target="_blank" rel="noopener noreferrer"
                 data-testid="ga-livestream"
                 className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-[0.16em] uppercase">
                {({ en: "Watch livestream", da: "Se livestream", es: "Ver en directo" })[lang]}
              </a>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Fact({ k, v, testid }) {
  if (!v) return null;
  return (
    <div data-testid={testid}>
      <dt className="label-mono text-white/55 tracking-[0.22em] text-[10px]">{k}</dt>
      <dd className="mt-2 font-editorial text-xl md:text-2xl leading-tight tracking-tight">{v}</dd>
    </div>
  );
}

function formatDateLong(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    const locale = { da: "da-DK", en: "en-GB", es: "es-ES" }[lang] || "en-GB";
    return d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

function formatTimeRange(start, end) {
  if (!start) return "";
  return end ? `${start} – ${end}` : start;
}

// Natural-language time zone label — never render raw IANA technical name.
function tzLabel(tz, lang) {
  const norm = (tz || "").toLowerCase();
  if (norm.includes("copenhagen") || norm === "europe/copenhagen") {
    return ({ en: "Copenhagen time", da: "dansk tid", es: "hora de Copenhague" })[lang] || "Copenhagen time";
  }
  return tz || "";
}
